import { useState } from 'react'
import { motion } from 'framer-motion'
import { Wrench, Power, Calendar, Zap, Clock, MessageSquare, Eye, ShieldCheck, AlertTriangle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fadeInUp, cardReveal } from '@/lib/motion'
import { Drawer } from 'vaul'
import { useToast } from '@/components/shared/Toast'

type MaintenanceType = 'scheduled' | 'emergency'

const defaultMessage = '系統正在進行維護，請稍後再試。您的餘額是安全的。'

export function AdminMaintenanceScreen() {
  const { toast } = useToast()
  const [enabled, setEnabled] = useState(false)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [type, setType] = useState<MaintenanceType>('scheduled')
  const [eta, setEta] = useState('約 30 分鐘')
  const [message, setMessage] = useState(defaultMessage)

  const handleToggleClick = () => {
    setConfirmOpen(true)
  }

  const handleConfirmToggle = () => {
    const newState = !enabled
    setEnabled(newState)
    setConfirmOpen(false)
    toast({ type: newState ? 'warning' : 'success', title: newState ? '維護模式已啟用' : '維護模式已關閉' })
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={{ hidden: { opacity: 0 }, visible: { opacity: 1, transition: { staggerChildren: 0.06 } } }}
      className="p-4 pb-8 flex flex-col gap-5"
    >
      {/* Current Status */}
      <motion.div variants={cardReveal} className="p-5 rounded-2xl glass-card space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', enabled ? 'bg-amber-500/10' : 'bg-emerald-500/10')}>
              <Power className={cn('w-5 h-5', enabled ? 'text-amber-400' : 'text-emerald-400')} />
            </div>
            <div>
              <h2 className="text-sm font-bold text-foreground">維護模式</h2>
              <p className={cn('text-xs font-bold', enabled ? 'text-amber-400' : 'text-emerald-400')}>
                {enabled ? '維護模式啟用中' : '系統正常運行'}
              </p>
            </div>
          </div>
          {/* Toggle */}
          <button
            onClick={handleToggleClick}
            className={cn(
              'w-14 h-8 rounded-full transition-colors duration-200 relative flex-shrink-0',
              enabled ? 'bg-amber-500' : 'bg-foreground/10'
            )}
          >
            <div className={cn(
              'absolute top-1 w-6 h-6 rounded-full bg-white shadow-md transition-transform duration-200',
              enabled ? 'translate-x-7' : 'translate-x-1'
            )} />
          </button>
        </div>
        {enabled && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-amber-500/10 border border-amber-500/20">
            <Wrench className="w-4 h-4 text-amber-400 flex-shrink-0" />
            <p className="text-[11px] text-amber-300">啟用後，所有玩家將看到維護公告，無法進入遊戲。</p>
          </div>
        )}
      </motion.div>

      {/* Settings */}
      <motion.section variants={fadeInUp} className="space-y-3">
        <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-1">維護設定</h3>

        {/* Type Selector */}
        <motion.div variants={cardReveal} className="p-4 rounded-2xl glass-card space-y-3">
          <p className="text-xs font-bold text-foreground">維護類型</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => setType('scheduled')}
              className={cn(
                'flex items-center gap-2.5 p-3 rounded-xl border transition-colors',
                type === 'scheduled' ? 'border-gold/40 bg-gold/5' : 'border-foreground/10 bg-surface-elevated'
              )}
            >
              <Calendar className={cn('w-4 h-4', type === 'scheduled' ? 'text-gold' : 'text-muted-foreground')} />
              <span className={cn('text-xs font-bold', type === 'scheduled' ? 'text-gold' : 'text-muted-foreground')}>
                排程維護
              </span>
            </button>
            <button
              onClick={() => setType('emergency')}
              className={cn(
                'flex items-center gap-2.5 p-3 rounded-xl border transition-colors',
                type === 'emergency' ? 'border-red-500/40 bg-red-500/5' : 'border-foreground/10 bg-surface-elevated'
              )}
            >
              <Zap className={cn('w-4 h-4', type === 'emergency' ? 'text-red-400' : 'text-muted-foreground')} />
              <span className={cn('text-xs font-bold', type === 'emergency' ? 'text-red-400' : 'text-muted-foreground')}>
                緊急維護
              </span>
            </button>
          </div>
        </motion.div>

        {/* ETA */}
        <motion.div variants={cardReveal} className="p-4 rounded-2xl glass-card space-y-2">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-muted-foreground" />
            <p className="text-xs font-bold text-foreground">預計完成時間</p>
          </div>
          <input
            type="text"
            value={eta}
            onChange={(e) => setEta(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-surface-elevated border border-foreground/10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-gold/40"
          />
        </motion.div>

        {/* Custom Message */}
        <motion.div variants={cardReveal} className="p-4 rounded-2xl glass-card space-y-2">
          <div className="flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-muted-foreground" />
            <p className="text-xs font-bold text-foreground">用戶公告訊息</p>
          </div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            className="w-full px-3 py-2 rounded-lg bg-surface-elevated border border-foreground/10 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-gold/40 resize-none"
          />
        </motion.div>
      </motion.section>

      {/* Preview */}
      <motion.section variants={fadeInUp} className="space-y-3">
        <div className="flex items-center gap-2 px-1">
          <Eye className="w-3.5 h-3.5 text-muted-foreground" />
          <h3 className="text-xs font-bold text-muted-foreground uppercase tracking-wider">用戶端預覽</h3>
        </div>
        <motion.div variants={cardReveal} className="p-6 rounded-2xl glass-card flex flex-col items-center text-center space-y-4">
          <div className={cn(
            'w-16 h-16 rounded-2xl flex items-center justify-center',
            type === 'emergency' ? 'bg-red-500/10' : 'bg-amber-500/10'
          )}>
            <Wrench className={cn('w-8 h-8', type === 'emergency' ? 'text-red-400' : 'text-amber-400')} />
          </div>
          <div className="space-y-2">
            <h4 className="text-lg font-bold text-foreground">系統維護中</h4>
            <p className="text-sm text-muted-foreground leading-relaxed">{message}</p>
          </div>
          {eta && (
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg bg-surface-elevated">
              <Clock className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-mono text-foreground">{eta}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 pt-2">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400" />
            <span className="text-[11px] text-emerald-400 font-bold">您的餘額是安全的</span>
          </div>
        </motion.div>
      </motion.section>

      {/* Confirmation Drawer */}
      <Drawer.Root open={confirmOpen} onOpenChange={(open) => { if (!open) setConfirmOpen(false) }}>
        <Drawer.Portal>
          <Drawer.Overlay className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50" />
          <Drawer.Content className="fixed bottom-0 left-0 right-0 z-50 outline-none">
            <div className="bg-surface-elevated rounded-t-3xl border-t border-foreground/10">
              <div className="flex justify-center py-3">
                <div className="w-10 h-1.5 rounded-full bg-foreground/20" />
              </div>
              <div className="px-6 pb-8 safe-area-bottom space-y-5">
                <Drawer.Title className="font-display text-xl font-semibold text-foreground">
                  {enabled ? '確定要結束維護模式？' : '確定要啟用維護模式？'}
                </Drawer.Title>

                <div className={cn(
                  'p-4 rounded-xl border flex items-start gap-3',
                  enabled ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-amber-500/10 border-amber-500/20'
                )}>
                  <AlertTriangle className={cn('w-5 h-5 flex-shrink-0 mt-0.5', enabled ? 'text-emerald-400' : 'text-amber-400')} />
                  <p className={cn('text-sm', enabled ? 'text-emerald-300' : 'text-amber-300')}>
                    {enabled ? '系統將恢復正常運作' : '所有玩家將被強制離線'}
                  </p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <button
                    onClick={() => setConfirmOpen(false)}
                    className="h-14 glass-card rounded-2xl font-semibold text-foreground"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleConfirmToggle}
                    className={cn(
                      'h-14 rounded-2xl font-bold text-white',
                      enabled ? 'bg-emerald-500' : 'bg-amber-500'
                    )}
                  >
                    確認
                  </button>
                </div>
              </div>
            </div>
          </Drawer.Content>
        </Drawer.Portal>
      </Drawer.Root>
    </motion.div>
  )
}
